import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useRef } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { updateWatchedFieldsUtils } from "src/app/features/Site/SiteAndSiteGroupUtils";
import { useSiteGroupLoadQuery } from "src/app/features/SiteGroup/services/server/SiteGroupApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { ID } from "../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useSiteSaveMutation } from "../../services/server/SiteApi";
import type { SiteEntity } from "../../types/SiteEntity";

export const SiteCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("site");

    const [formData, setFormData] = useState<SiteEntity>({} as SiteEntity);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useSiteSaveMutation();
    const prevParentUnitIdRef = useRef<ID | undefined>(undefined);
    const formDataRef = useRef(formData);

    const onChangeCallback = useCallback((newData: SiteEntity) => {
        setFormData({ ...newData, statusCheckReadOnly: formDataRef.current?.statusCheckReadOnly });
    }, []);

    // HANDLE WATCH FIELDS
    const { data: siteGroupEntity } = useSiteGroupLoadQuery(
        { entityId: formData.parentUnit ? `${formData.parentUnit}` : undefined },
        { skip: !formData?.parentUnit },
    );

    const updateWatchedFields = useCallback(() => {
        const currentParentUnitId = formData?.parentUnit;

        if (!currentParentUnitId || !siteGroupEntity || !prevParentUnitIdRef) return null;

        prevParentUnitIdRef.current = currentParentUnitId;
        if (!siteGroupEntity || !formDataRef.current) return null;

        return updateWatchedFieldsUtils(siteGroupEntity, formData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteGroupEntity]);

    useEffect(() => {
        const currentParentUnitId = formData?.parentUnit;

        if (!siteGroupEntity || !prevParentUnitIdRef || !currentParentUnitId) {
            return;
        }

        const prevParentUnitId = prevParentUnitIdRef.current;

        if (currentParentUnitId !== prevParentUnitId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [formData, updateWatchedFields, siteGroupEntity]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "Site",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.SITES}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<SiteEntity>
                    name="SiteCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
